import React, { useContext, useEffect, useMemo, useCallback, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IWatchContext, WatchContext } from '../../../watchState/watchContext';
import { UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs, EntityStatus } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';

export const RestaurantSelect: React.FC = () => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const { setIDRestaurant, IDSelectedCustomer, IDSelectedRestaurant } = useContext<IWatchContext>(WatchContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		IDSelectedCustomer && hasAccess ? getRestaurantsShortInfo : null,
		{
			customerId: IDSelectedCustomer || '',
		},
	);
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);

	useEffect(() => {
		refetch();
	}, [IDSelectedCustomer]);

	const restaurants = useMemo(() => {
		const items = hasAccess ? response?.data.restaurants : loggedUser.restaurants || [];
		const visibleItems = showArchived
			? items
			: items?.filter(
					(restaurant) => restaurant.status === EntityStatus.Active || restaurant.id === IDSelectedRestaurant,
			  );

		return visibleItems?.map((restaurant) => ({
			...restaurant,
			additionalContent: restaurant.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [hasAccess, response, loggedUser, showArchived, IDSelectedRestaurant]);

	const value = IDSelectedCustomer && !loading ? IDSelectedRestaurant : undefined;

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	return (
		<OutsideFormSelect
			containerClassName="!min-h-min w-px"
			className="!mt-0"
			testId="RestaurantSelect"
			name="RestaurantSelect"
			label="restaurantLabel"
			onChange={setIDRestaurant}
			onRefetch={refetch}
			value={value}
			options={restaurants}
			listItemHeight={restaurants?.length}
			disabled={!IDSelectedCustomer || loading}
			loading={loading}
			fetchError={responseError}
			withValidation={false}
			keepSearchWithDropdownRender={true}
			dropdownRender={renderSelectMenu}
		/>
	);
};
